/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { setLocale } from "@microsoft/shifts-experiences-shared";
import { AxiosResponse } from "axios";
import RestClient from "sh-rest-client";
import { setCurrentLocale } from "sh-strings/actions/setCurrentLocale";
import { setStringModule } from "sh-strings/actions/setStringModule";

/**
 * To add new languages:
 * - Add languages to the moment locale inclusion list src/client/react/config/webpack.common.js
 * - Add languages to supportedCultures array in src/server/Startup.cs
 * - Add languages to ReactStringsAssets in src/server/Models/AppSettings.cs
 * - Update legal strings in string resources:
 *      There are certain legal-related strings that are locked and need to be localized by Legal and not regular Localization.
 *      Ask PM to get the localized versions of these strings from Legal, and then ask our Localization contact to update and lock these strings
 *      in Touchdown.
 *      Current string resources (search for "do not localize"):  sectionLegalConsentProvisioning, kronosTermsOfService
 * - Verify no changes need to be made for our Touchdown Build Task
 *      In VSO, go to our "StaffHub Web CI-TD" build:  https://olsapps.visualstudio.com/Shiftr/_apps/hub/ms.vss-ciworkflow.build-ci-hub?_a=edit-build-definition&id=769
 *      Note:  Normally these settings won't need to be changed unless we change the location or file names for our strings files.
 * - In Web's Touchdown Build - Dashboard configs, add/ensure the new languages are in our "Extent of Localization (EOL)" and "Valid Cultures" settings.
 *      http://tdbuild:40/Home/ConfigSettings?TeamID=244#
 * - For info about Touchdown:  http://aka.ms/tdbuild
 * - To verify language handling in builds, add the "?ui-culture={locale code}" query parameter to the StaffHub urls to switch languages.
 */

/**
 * Process Strings and set them as string Modules.
 * @param stringsFile
 */
function processStrings(stringsFile: any) {
    for (const name of Object.keys(stringsFile)) {
        const stringModule = {
            name: name,
            strings: new Map<string, string>()
        };

        const jsonStrings = stringsFile[name];
        for (const key of Object.keys(jsonStrings)) {
            // skip all the comments
            if (!key.startsWith("_")) {
                stringModule.strings.set(key, jsonStrings[key]);
            }
        }
        // Set the string module in store
        setStringModule(stringModule);
    }
}

/**
 * Initialize String Modules from preFetched content
 */
function initializeStringsFromContent(locale: string) {
    // Parse the prefetched content
    const stringsFileContent = JSON.parse(window.sa.stringsFileContent);
    setCurrentLocale(locale);
    processStrings(stringsFileContent.strings);
}

/**
 * Downloads the localized strings and initializes the StringsStore(). Missing localized strings are replaced with the en version.
 */
export default async function initializeStrings(locale: string): Promise<void> {
    setLocale(locale);

    if (UNIT_TEST_MODE) {
        const enStrings = require("../../../assets/strings/strings.json");
        processStrings(enStrings.strings);
        return;
    }

    if (window.sa.stringsFileContent) {
        // if preFetched content exists, use it directly instead of loading strings from network
        initializeStringsFromContent(locale);
    } else {
        // if there is no prefetched data, fetch strings from network
        const response: AxiosResponse = await RestClient.getClientStrings();
        setCurrentLocale(locale);
        const stringsFile = response.data.strings;
        processStrings(stringsFile);
    }
}